import moment from "moment";
import { first, includes, replace, toNumber, groupBy, isEmpty } from "lodash";
import { BASE_CURRENCY_FORMAT, BASE_DATE_FORMAT } from "@/constants/common.js";
import Dinero from "dinero.js";
import axios from "axios";
import {
  addSuccessMessage,
  getImpersonateUserId,
  getToken
} from "@/services/utils";
import store from "@/store";
import { POST_GLOBAL_MESSAGE } from "@/store/modules/global/action-types";

export const compareDates = (a, b, format) => {
  const formattedA = moment(a, format);
  const formattedB = moment(b, format);

  return formattedA.diff(formattedB);
};

export const getMonetaryValueAsNumber = value => {
  return toNumber(replace(value.substring(1), /,/g, ""));
};

export const getMonetaryValue = value => {
  return Dinero({
    amount: toNumber(value)
  }).toUnit();
};

export const getNumberAsMonetaryValue = (value, currency) => {
  if (!value) value = 0;
  return Dinero({
    amount: toNumber(value),
    currency
  }).toFormat(BASE_CURRENCY_FORMAT);
};

export const getAmountInApiFormat = value => {
  return Number(value * 100);
};

export const getAmountInDisplayFormat = value => {
  return Number(value / 100);
};

export const getMonetaryDisplayFormat = value => {
  if (!value) {
    return 0;
  }
  return Dinero({
    amount: toNumber(value)
  }).toFormat("0,0.00");
};

export const getMonetaryAsNumber = value => {
  return toNumber(replace(value, /,/g, ""));
};

export const getAmountInMonetaryApiFormat = value => {
  return getAmountInApiFormat(value).toFixed(2);
};

export const getAmountInMonetaryDisplayFormat = value => {
  return getAmountInDisplayFormat(value).toFixed(2);
};

export const getNameInitials = ({ firstName, lastName }) =>
  firstName && lastName
    ? first(firstName).toUpperCase() + first(lastName).toUpperCase()
    : "-";

export const sortTableItems = (items, arr, isDescArray, options) => {
  const { monetaryValuesLabels, dateValuesLabels } = options;
  const isDesc = first(isDescArray);
  const index = first(arr);
  const sortDirection = isDesc ? -1 : 1;
  return items.sort((a, b) => {
    let firstValue = a[index];
    let secondValue = b[index];
    if (includes(dateValuesLabels, index)) {
      return (
        compareDates(firstValue, secondValue, BASE_DATE_FORMAT) * sortDirection
      );
    }
    if (index === "id") {
      firstValue = Number(firstValue);
      secondValue = Number(secondValue);
    }
    if (includes(monetaryValuesLabels, index)) {
      firstValue = getMonetaryValueAsNumber(firstValue);
      secondValue = getMonetaryValueAsNumber(secondValue);
    }
    return (firstValue < secondValue ? -1 : 1) * sortDirection;
  });
};

export const getSortingParam = (paramNameArray, sortDescArray) => {
  const paramName = first(paramNameArray);
  const sortingDirection = first(sortDescArray) ? "-" : "+";
  return paramName && `${sortingDirection}${paramName}`;
};

export const getTimeForHumans = date => {
  const today = moment();
  if (today.diff(date, "days") > 1) {
    return moment(date).format("Do MMMM YYYY hh:mm");
  }

  return moment(date).fromNow();
};

export const groupByDate = ({
  items,
  key = "startDate",
  format = "DD/MM/YYYY"
}) =>
  items.reduce((groupedByDate, item) => {
    const day = moment(item[key]).format(format);
    if (groupedByDate[day]) {
      groupedByDate[day].push(item);
      return groupedByDate;
    }
    return { ...groupedByDate, [day]: [item] };
  }, {});

export const groupByMonth = items =>
  groupBy(items, item => moment(first(item).startDate).format("MMM"));

export const deduplicate = array => [...new Set(array)];

export const compareBy = key => (firstObject, secondObject) =>
  firstObject && secondObject && firstObject[key] === secondObject[key];

export const copyState = keys =>
  keys.reduce(
    (mapStateObject, currentKey) => ({
      ...mapStateObject,
      [currentKey]: state => state[currentKey]
    }),
    {}
  );

export const forceFileDownload = (response, fileName) => {
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  document.body.appendChild(link);
  link.setAttribute("download", fileName);
  link.click();
};

export const fileToDownload = async (file, data = {}, isBuffer = true) => {
  const url = `${file.url}`;
  const headers = {
    Authorization: `Bearer ${getToken()}`,
    "Content-Type": "application/json"
  };
  const impersonateUserId = getImpersonateUserId();
  if (impersonateUserId) {
    headers["X-Impersonate"] = impersonateUserId;
  }
  let config = {
    baseURL: url,
    timeout: 30000,
    headers: headers
  };
  if (isBuffer) {
    config.responseType = "arraybuffer";
  }
  const instance = axios.create(config);

  try {
    const response = isEmpty(data)
      ? await instance.get()
      : await instance.post(url, data);
    if (isBuffer) {
      forceFileDownload(response, file.fileName);
    } else {
      addSuccessMessage(
        response.data.meta.title || null,
        response.data.meta.body || null
      );
    }
  } catch (error) {
    console.error(error);
    store.dispatch(POST_GLOBAL_MESSAGE, {
      snackbar: true,
      type: "error",
      title: `An error occurred downloading file ${file.fileName}`
    });
  }
};

export const scrollToPageTop = () => {
  const appContent = document.getElementById("app-content");
  if (appContent) {
    appContent.scrollTop = 0;
  }
};

export const scrollToError = () => {
  const element = document.querySelector(".v-messages__message");
  element.scrollIntoView({
    behavior: "smooth",
    block: "center"
  });
};

export const getListedArray = array => {
  return array.filter(Boolean).join(", ");
};
